import type { FragmentOf } from '@/utilities/gql';
import {
  ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
  ROUTE_PUBLIC_NARRATIVE_CREATE,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
} from '@/constants/routes';
import { getPath } from '@/router/utils';
import { isAfter } from 'date-fns';
import { useUserProfile } from '@/hooks/useUserProfile';
import type { NARRATIVE_EDIT_BUTTON_FRAGMENT } from '../narrativeEditButtonFragment';

type Props = {
  narrative: FragmentOf<typeof NARRATIVE_EDIT_BUTTON_FRAGMENT>;
  companyId: string;
  returnPath?: string;
};

export const useNarrativeEditLink = ({
  narrative,
  companyId,
  returnPath,
}: Props) => {
  const { publicId: currentUserPublicId } = useUserProfile();
  const {
    id: narrativeId,
    owner,
    latestPublishedUpdate,
    earliestPublishedUpdate,
  } = narrative;
  const { publicId } = owner;
  const isMyNarrative = currentUserPublicId === publicId;

  const getMyNarrativeEditHref = () => {
    if (!isMyNarrative) return null;

    const isEditableWithinTimelock =
      latestPublishedUpdate?.willLockAt &&
      isAfter(new Date(latestPublishedUpdate.willLockAt), new Date());

    const isNarrativeDraft = !latestPublishedUpdate;
    const isNarrative =
      isNarrativeDraft ||
      (latestPublishedUpdate &&
        earliestPublishedUpdate?.id === latestPublishedUpdate?.id &&
        isEditableWithinTimelock);

    if (isNarrative)
      return getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: {
          id: narrativeId,
          query: {
            company: companyId ? companyId.toLowerCase() : undefined,
            returnPath,
          },
        },
      });

    return getPath({
      type: ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
      payload: {
        companyid: companyId,
        query: {
          returnPath,
        },
      },
    });
  };

  return {
    editHref: getMyNarrativeEditHref(),
    createHref: getPath({
      type: ROUTE_PUBLIC_NARRATIVE_CREATE,
      payload: {
        query: {
          company: companyId,
          returnPath,
        },
      },
    }),
  };
};
